import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import BusinessPlan from "@/components/BusinessPlan";
import LemonBotPanel from "@/components/LemonBotPanel";
import ResourceLibrary from "@/components/ResourceLibrary";
import {
  BUSINESS_TEMPLATES,
  BUSINESS_TEMPLATES_FOLDER,
} from "@/data/externalResources";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default placeholder logo
const PLACEHOLDER_LOGO = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='45' fill='%23FCD34D'/%3E%3Cellipse cx='50' cy='50' rx='35' ry='40' fill='%23FBBF24'/%3E%3Cpath d='M50 15 Q55 5 60 15' stroke='%2316A34A' stroke-width='3' fill='none'/%3E%3Cellipse cx='55' cy='12' rx='8' ry='5' fill='%2322C55E'/%3E%3C/svg%3E";

function BusinessPlanPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { studentId } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    loadBusinessPlan();
    loadLogo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const loadBusinessPlan = async () => {
    try {
      setLoading(true);
      const endpoint = studentId 
        ? `${API}/business-plan/${studentId}`
        : `${API}/business-plan/me`;
      
      const response = await axios.get(endpoint);
      setPlan(response.data);
    } catch (err) {
      console.error("Error loading business plan:", err);
      setError(err.response?.data?.detail || "Failed to load business plan");
    } finally {
      setLoading(false);
    }
  };

  const loadLogo = async () => {
    try {
      const endpoint = studentId 
        ? `${API}/logo/${studentId}`
        : `${API}/logo/me`;
      const response = await axios.get(endpoint);
      if (response.data.has_logo) {
        setLogo(response.data.logo);
      }
    } catch (err) {
      console.log("No logo found");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">😕</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Oops!</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-yellow-500 text-white rounded-lg font-semibold hover:bg-yellow-600 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 text-gray-600 hover:text-gray-800 font-semibold flex items-center gap-2"
          data-testid="back-btn"
        >
          ← Back
        </button>

        {/* Workbook Ready Banner */}
        {plan?.is_complete && (
          <div className="bg-gradient-to-r from-green-400 to-teal-500 rounded-2xl p-6 mb-8 text-white">
            <div className="flex items-center justify-center gap-4">
              {/* Logo Display */}
              <div 
                className="w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center overflow-hidden border-2 border-white flex-shrink-0"
                data-testid="workbook-logo"
              >
                <img
                  src={logo ? `data:image/png;base64,${logo.image_base64}` : PLACEHOLDER_LOGO}
                  alt="Business Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="text-center">
                <div className="text-3xl mb-1">📓</div>
                <h1 className="text-2xl font-bold mb-1">Your Business Workbook</h1>
                <p className="opacity-90 text-sm">
                  Your personal record of reflections and answers from all 7 sessions.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Educator viewing note */}
        {studentId && user?.role !== "student" && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800 text-sm">
              <strong>Educator View:</strong> You are viewing {plan?.student_name}&apos;s workbook (read-only).
            </p>
          </div>
        )}

        {/* Business Workbook Component */}
        <BusinessPlan 
          plan={plan} 
          studentName={plan?.student_name || user?.name || "Student"}
          onClose={() => navigate(-1)}
          showActions={!studentId || user?.role === "student"}
        />

        {/* LemonBot inline coach for the workbook (only for the student themselves) */}
        {!studentId && user?.role === "student" && (
          <div className="mt-6">
            <LemonBotPanel
              activity="workbook"
              title="LemonBot · Workbook coach"
              subtitle="Stuck on a reflection? I'll ask you 2 questions, not write it for you."
              quickActions={[
                {
                  intent: "explain_easier",
                  label: "Stuck on a reflection",
                  prompt:
                    "I'm stuck on one of my workbook reflections. Ask me 2 short questions that will help me find my answer.",
                },
                {
                  intent: "give_example",
                  label: "Show me an example",
                  prompt:
                    "Show me ONE example of a strong session reflection from another (imaginary) student. Keep it under 50 words and label which session it's for.",
                },
                {
                  intent: "business_plan_help",
                  label: "Connect to my plan",
                  prompt:
                    "Help me see how my workbook reflections connect to my Mini Business Plan. What's one thread I should pull through more clearly?",
                },
              ]}
            />
          </div>
        )}

        {/* Business Templates Library — student-facing helpers */}
        <div className="mt-8">
          <ResourceLibrary
            title="Business Templates"
            description="Worksheets, calculators, and pitch templates to help you build out your plan. Click Get to download, or open the full folder in Drive."
            folderUrl={BUSINESS_TEMPLATES_FOLDER}
            files={BUSINESS_TEMPLATES}
            testId="business-plan-templates"
            accent="yellow"
          />
        </div>
      </div>
    </div>
  );
}

export default BusinessPlanPage;
